import type { FC } from "react";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

import clsx from "clsx";

import Button from "@/design-system/button";
import { useViewportInfoContext } from "@/src/contexts";
import type { IAjax } from "@Components/Hooks/useAjax";
import useAjax from "@Components/Hooks/useAjax";
import { urlFormat } from "@Components/Modules/Helpers";
import type { IPanel } from "@Components/Panel";
import { Panel } from "@Components/Panel";
import * as stylex from "@stylexjs/stylex";

import type { IComponentViewState } from "../../common/ComponentViewState";
import type { IDescriptionElement, IDiscountTile, ITitleElement } from "../DiscountTile";
import DiscountTile, { DiscountTileType } from "../DiscountTile";
import type { IDiscountsCarousel } from "../DiscountsCarousel";
import { DiscountsCarousel } from "../DiscountsCarousel";
import { colors } from "./../../../styles/tokens.stylex";

import * as css from "./DiscountsPanel.module.css";

export interface IDiscountsPanelResult {
    carousels: IDiscountsCarousel[];
    nextPageAction?: string;
}

export interface IDiscountsPanel
    extends IAjax<IDiscountsPanelResult>, IPanel, IComponentViewState<IDiscountsPanelViewState> {
    title: string;
    showMoreText?: string;
    disableLazyLoading?: boolean;
    hideIfEmpty?: boolean;
    tileTitleElement: keyof ITitleElement;
    tileDescriptionElement: keyof IDescriptionElement;
    categoryId?: string;
}

interface IDiscountsPanelState {
    carousels: IDiscountsCarousel[] | null;
    nextPageAction?: string;
    isInitialRequest: boolean;
}

const loadingTiles: IDiscountsCarousel = {
    discounts: [
        {
            ...DiscountTile.defaultProps,
            id: "loading-1",
            type: DiscountTileType.Unknown,
            isLoadingLayout: true,
        } as IDiscountTile,
        {
            ...DiscountTile.defaultProps,
            id: "loading-2",
            type: DiscountTileType.Unknown,
            isLoadingLayout: true,
        } as IDiscountTile,
    ],
};

export interface IDiscountsPanelViewState extends IDiscountsPanelResult {
    title?: string;
}

const DiscountsPanel: FC<IDiscountsPanel> = (props) => {
    const [state, setState] = useState<IDiscountsPanelState>({
        carousels: props.initialData?.carousels || props.componentState?.carousels || null,
        nextPageAction:
            props.initialData?.nextPageAction || props.componentState?.nextPageAction || undefined,
        isInitialRequest: true,
    });

    const { isMobileOrTablet } = useViewportInfoContext();

    const nextPageResults = (response: IDiscountsPanelResult): void => {
        if (!response || !response.carousels) {
            setState({ ...state, nextPageAction: undefined });
            return;
        }

        setState({
            ...state,
            carousels: [...response.carousels],
            nextPageAction: response.nextPageAction,
            isInitialRequest: false,
        });

        if (props.success) {
            props.success(response);
        }
    };
    const { request: nextPageAction, errorContainer: errorContainerNextPage } =
        useAjax<IDiscountsPanelResult>({
            ajaxAction: urlFormat(state.nextPageAction, { group: !isMobileOrTablet }),
            success: nextPageResults,
        });

    const { data, isLoading, request, errorContainer } = useAjax<IDiscountsPanelResult>({
        ajaxAction: props.ajaxAction,
        initialData: state as IDiscountsPanelResult,
    });

    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: "35%",
        initialInView: props.disableLazyLoading || !!props.initialData,
    });

    useEffect(() => {
        if (!state.carousels && inView) {
            request({ ...props, group: !isMobileOrTablet });
        }
    }, [inView]);

    useEffect(() => {
        if (!state.isInitialRequest && inView) {
            request({ ...props, group: !isMobileOrTablet });
        }
    }, [isMobileOrTablet, inView]);

    useEffect(() => {
        if (data) {
            nextPageResults(data);
        }
    }, [data]);

    if (props.hideIfEmpty && !isLoading && (!state.carousels || state.carousels.length === 0)) {
        return null;
    }

    const renderCarousel = (carousel: IDiscountsCarousel, index: number, loadingLayout = false) => {
        if (!carousel || !carousel.discounts) {
            return null;
        }

        if (!loadingLayout) {
            carousel.discounts.forEach((d) => {
                d.descriptionElement = props.tileDescriptionElement;
            });
        }

        return <DiscountsCarousel key={index} {...carousel} disableLazyLoading={props.disableLazyLoading} />;
    };

    const shouldRenderInitialCarousel = !props.disableLazyLoading
        ? (isLoading || !inView) && [renderCarousel(loadingTiles, 0, true)]
        : null;

    if (!isLoading && state.carousels?.length === 0) {
        return null;
    }

    return (
        <Panel {...props} className={clsx(css.discountsPanelComponent, props.className)} ref={ref}>
            {shouldRenderInitialCarousel ||
                (state.carousels ? state.carousels.map((s, i) => renderCarousel(s, i)) : null)}
            {state.nextPageAction && (
                <Button
                    as="button"
                    key={state.nextPageAction}
                    variant="none"
                    size="None"
                    onClick={nextPageAction}
                    styleX={styles.link}
                >
                    {props.showMoreText}
                </Button>
            )}
            {errorContainer}
            {errorContainerNextPage}
        </Panel>
    );
};

const styles = stylex.create({
    link: {
        alignSelf: "center",
        color: colors.fg_Accent,
    },
});

export default DiscountsPanel;
